import React, { useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useColorPalette } from '../../hooks/useColorPalette';

/**
 * Modal che mostra la conferma prima dell'eliminazione di un indirizzo
 * Se ci sono impianti associati, li mostra in dettaglio
 * @param {boolean} isOpen - Stato di apertura della modale
 * @param {function} toggle - Funzione per chiudere/aprire la modale
 * @param {array} systems - Array degli impianti associati all'indirizzo (può essere vuoto)
 * @param {function} onConfirm - Funzione da eseguire alla conferma dell'eliminazione
 * @param {object} addressToDelete - Dati dell'indirizzo da eliminare
 */
const AddressSystemsModal = ({ isOpen, toggle, systems, onConfirm, addressToDelete = {} }) => {
    // Utilizzo dell'hook per accedere alla palette di colori
    const palette = useColorPalette();
    
    // Log per tracciare le aperture e chiusure della modale
    useEffect(() => {
        console.log('[DEBUG] AddressSystemsModal - Stato modale cambiato:', { 
            isOpen, 
            sistemiAssociati: systems?.length || 0 
        });
        
        if (isOpen && (!systems || systems.length === 0)) {
            console.warn('[DEBUG] AddressSystemsModal - Modale aperta ma senza sistemi associati!');
        }
        
        if (isOpen) {
            console.log('[DEBUG] AddressSystemsModal - Dettaglio sistemi:', systems);
        }
    }, [isOpen, systems]);

    // Funzione per formattare la lista dei tipi di impianto
    const formatSystemTypes = (typesList) => {
        if (!typesList || typesList.length === 0) return 'Nessun tipo specificato';
        return typesList.map(type => type.typeLabel).join(', ');
    };

    console.log('[DEBUG] AddressSystemsModal - Rendering modale con:', { isOpen });
    
    // Se la modale non è aperta, non renderizziamo nulla
    if (!isOpen) return null;
    
    // Utilizziamo un approccio più diretto con CSS in-line per garantire la visibilità
    const modalStyles = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
    };
    
    const modalContentStyles = {
        backgroundColor: 'white',
        borderRadius: '5px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    };

    return (
        <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
            <div style={modalContentStyles}>
                {/* Header */}
                <div style={{ 
                    backgroundColor: palette.secondary, 
                    color: 'white', 
                    padding: '1rem', 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTopLeftRadius: '5px',
                    borderTopRightRadius: '5px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="fa-solid fa-exclamation-triangle me-2 text-warning"></i>
                        <span className="fw-bold">
                            {systems?.length > 0 
                                ? 'Attenzione - Eliminazione indirizzo con impianti associati' 
                                : 'Conferma eliminazione indirizzo'}
                        </span>
                    </div>
                    <button 
                        onClick={toggle} 
                        style={{ 
                            background: 'transparent', 
                            border: 'none', 
                            color: 'white',
                            fontSize: '1.5rem',
                            cursor: 'pointer'
                        }}
                    >
                        &times;
                    </button>
                </div>
                
                {/* Body */}
                <div style={{ padding: '1.5rem' }}>
                    {/* Contenuto diverso in base alla presenza di impianti associati */}
                    {systems?.length > 0 ? (
                        <>
                            <p style={{ marginBottom: '1rem' }}>
                                Questo indirizzo è attualmente associato a {systems.length} {systems.length === 1 ? 'impianto' : 'impianti'}.
                                Se procedi con l'eliminazione, l'associazione tra {systems.length === 1 ? 'questo impianto' : 'questi impianti'} e l'indirizzo verrà persa.
                            </p>
                            
                            <div style={{ 
                                border: '1px solid #dee2e6', 
                                borderRadius: '5px', 
                                padding: '1rem',
                                backgroundColor: '#f8f9fa',
                                marginBottom: '1rem'
                            }}>
                                <h6 style={{ marginBottom: '1rem', color: palette.primary }}>Impianti associati:</h6>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ 
                                        width: '100%', 
                                        borderCollapse: 'collapse',
                                        fontSize: '0.9rem'
                                    }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                                <th style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>#</th>
                                                <th style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>Titolo</th>
                                                <th style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>Indirizzo</th>
                                                <th style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>Tipi</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {systems.map((system, index) => (
                                                <tr key={system._id || index} style={{ 
                                                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' 
                                                }}>
                                                    <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{index + 1}</td>
                                                    <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{system.title}</td>
                                                    <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{system.customerAddressLabel}</td>
                                                    <td style={{ padding: '0.5rem', border: '1px solid #dee2e6' }}>{formatSystemTypes(system.typesList)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            
                            <div style={{ 
                                backgroundColor: '#fff3cd', 
                                color: '#856404', 
                                padding: '1rem', 
                                borderRadius: '5px',
                                marginBottom: '1rem'
                            }}>
                                <i className="fa-solid fa-info-circle me-2"></i>
                                Procedendo con l'eliminazione, dovrai riassociare manualmente gli impianti a un altro indirizzo.
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '1.5rem'
                            }}>
                                <i className="fa-solid fa-trash" style={{ 
                                    fontSize: '2rem', 
                                    color: palette.danger || '#dc3545',
                                    marginRight: '1rem' 
                                }}></i>
                                <h5 style={{ margin: 0 }}>Stai per eliminare questo indirizzo</h5>
                            </div>
                            
                            <div style={{ 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '5px',
                                padding: '1rem',
                                marginBottom: '1rem',
                                border: '1px solid #dee2e6'
                            }}>
                                <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                    Dettagli indirizzo:
                                </p>
                                <ul style={{ 
                                    listStyleType: 'none',
                                    padding: '0',
                                    margin: '0'
                                }}>
                                    <li><strong>Indirizzo:</strong> {addressToDelete?.address || 'N/D'}</li>
                                    {addressToDelete?.alias && <li><strong>Alias:</strong> {addressToDelete.alias}</li>}
                                    {addressToDelete?.addressTypeLabel && <li><strong>Tipo:</strong> {addressToDelete.addressTypeLabel}</li>}
                                </ul>
                            </div>
                        </>
                    )}
                    
                    {/* Messaggio comune per entrambi i casi */}
                    <div style={{ 
                        backgroundColor: '#e2f0fd', 
                        color: '#0c5460', 
                        padding: '1rem', 
                        borderRadius: '5px',
                        border: '1px solid #bee5eb'
                    }}>
                        <i className="fa-solid fa-info-circle me-2"></i>
                        <div style={{ fontWeight: 'bold' }}>
                            Per completare effettivamente l'eliminazione dell'indirizzo devi cliccare sul pulsante "Modifica cliente" in fondo alla pagina.
                        </div>
                    </div>
                </div>
                
                {/* Footer */}
                <div style={{ 
                    padding: '1rem',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.5rem',
                    borderTop: '1px solid #dee2e6'
                }}>
                    <button 
                        onClick={() => {
                            console.log('[DEBUG] AddressSystemsModal - Annulla cliccato');
                            toggle();
                        }}
                        style={{ 
                            backgroundColor: '#6c757d', 
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="fa-solid fa-times me-2"></i>
                        Annulla
                    </button>
                    <button 
                        onClick={() => {
                            console.log('[DEBUG] AddressSystemsModal - Elimina cliccato');
                            onConfirm();
                            toggle();
                        }}
                        style={{ 
                            backgroundColor: '#dc3545', 
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer'
                        }}
                    >
                        <i className="fa-solid fa-trash me-2"></i>
                        Elimina comunque
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddressSystemsModal;
